import { useEffect, useMemo } from 'react'

import { FiPlus } from 'react-icons/fi'
import type { MapRef } from 'react-map-gl/mapbox'
import { Link } from 'react-router-dom'

import Dropdown, { type DropdownOption } from '@/components/ui/dropdown/Dropdown'
import { LayoutSwitch, ListCounts, ListPageToolbar } from '@/components/ui/list-page/ListPageToolbar'
import { ListSearch } from '@/components/ui/list-page/ListSearch'

import { GeofenceFilter } from './filters/GeofenceFilter'
import { ShipmentFilter } from './filters/ShipmentFilter'
import { StatusFilter } from './filters/StatusFilter'
import { TrackerListControls } from './TrackerListControls'
import { useTrackerMapContext } from './TrackerMapContext'
import { TrackerSavedViews } from './TrackerSavedViews'
import { useCustomers } from './useCustomers'

interface TrackerMapToolbarProps {
  mapRef: React.RefObject<MapRef | null>
}

export const TrackerMapToolbar = ({ mapRef }: TrackerMapToolbarProps) => {
  const context = useTrackerMapContext()
  const { customers, isLoading: isLoadingCustomers } = useCustomers()
  const { filteredTrackers, viewportFilteredTrackers, allTrackers } = context

  const customerOptions: DropdownOption[] = useMemo(
    () => customers.map((c) => ({ value: c.id.toString(), label: c.name })),
    [customers]
  )
  const selectedCustomerOption = useMemo(
    () => customerOptions.find((o) => o.value === context.companyId?.toString()) ?? null,
    [customerOptions, context.companyId]
  )

  // Auto-select when the caller has exactly one customer, so trackers load
  // without a manual pick.
  useEffect(() => {
    if (!context.companyId && customers.length === 1) {
      context.setCompanyId(customers[0].id)
    }
  }, [customers, context])

  return (
    <ListPageToolbar
      title="Trackers"
      sheetIsOpen={context.detailSheetIsOpen}
      savedViews={<TrackerSavedViews mapRef={mapRef} />}
      search={
        <ListSearch
          id="search-trackers"
          placeholder="Search trackers..."
          value={context.searchTerm}
          onChange={context.setSearchTerm}
        />
      }
      counts={
        <ListCounts
          updatedAt={context.updatedAt}
          count={
            context.sidebarState.limitToMap && viewportFilteredTrackers.length !== filteredTrackers.length
              ? `${viewportFilteredTrackers.length.toLocaleString()} visible · ${filteredTrackers.length.toLocaleString()} matching · ${allTrackers.length.toLocaleString()} total`
              : `${filteredTrackers.length.toLocaleString()} of ${allTrackers.length.toLocaleString()} trackers`
          }
        />
      }
      scope={
        (isLoadingCustomers || customers.length > 0) && (
          // "Company" in the UI; the backing data is the customer table.
          <div className="hidden md:block w-[23rem]">
            <Dropdown
              options={customerOptions}
              selectedItem={selectedCustomerOption}
              handleSingleSelectItem={(o) => context.setCompanyId(o ? parseInt(o.value) : undefined)}
              placeholder={isLoadingCustomers ? 'Loading...' : 'Choose a company...'}
            />
          </div>
        )
      }
      listControls={<TrackerListControls />}
      layoutSwitch={
        <LayoutSwitch
          mode={context.sidebarState.layoutMode}
          onChange={(layoutMode) => context.setSidebarState((prev) => ({ ...prev, layoutMode }))}
        />
      }
      filters={
        <>
          <ShipmentFilter />
          <StatusFilter />
          <GeofenceFilter />
        </>
      }
      actions={
        <Link
          to="/admin/shipments/new"
          state={{ from: 'trackers' }}
          className="btn btn-sm btn-primary shrink-0"
          data-testid="add-shipment"
        >
          <FiPlus className="w-4 h-4" />
          Add Shipment
        </Link>
      }
    />
  )
}
